import React from 'react';
import PropTypes from 'prop-types';
import { has } from 'lodash';
import { Box, Tooltip, Typography, Link } from '@mui/material';
import { PlaceOutlined as MapsIcon, EventOutlined as EventIcon } from '@mui/icons-material';
import { FormattedMessage, useIntl } from 'react-intl';

import { moment } from '../../../../I18n';
import useWidth from '../../../../Hooks/useWidth';
import LocationLink from '../../../Maps/LocationLink';

function Tag({ icon, label }: any) {
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Box sx={{ color: 'text.secondary' }}>{icon}</Box>
      <Box>{label}</Box>
    </Box>
  );
}

function PostTags({ items }: any) {
  const intl = useIntl();

  const renderItem = (id: any, item: any) => {
    const hasLocation = has(item, 'lat') && has(item, 'lng');

    if (hasLocation) {
      return <Tag key={id} icon={<MapsIcon />} label={<LocationLink item={item} />} />;
    }

    if (['start_at', 'end_at'].every((key) => Object.keys(item).includes(key))) {
      let periodI18nStringID;

      if (moment(item.start_at).isSame(item.end_at, 'day')) {
        periodI18nStringID = 'POSTS.TAGS.PERIOD_SAME_DAY';
      } else {
        periodI18nStringID = useWidth('down', 'sm')
          ? 'POSTS.TAGS.PERIOD_SHORT'
          : 'POSTS.TAGS.PERIOD';
      }

      return (
        <Tag
          key={id}
          icon={<EventIcon />}
          label={
            <Typography variant="body2" color="textPrimary">
              <FormattedMessage
                id={periodI18nStringID}
                values={{
                  startAtDate: moment(item.start_at).format(
                    intl.formatMessage({ id: 'MOMENT.DATE.DEFAULT' }),
                  ),
                  startAtHour: moment(item.start_at).format('LT'),
                  endAtDate: moment(item.end_at).format(
                    intl.formatMessage({ id: 'MOMENT.DATE.DEFAULT' }),
                  ),
                  endAtHour: moment(item.end_at).format('LT'),
                  strong: (...chunks: any[]) => <strong>{chunks}</strong>,
                }}
              />
            </Typography>
          }
        />
      );
    }

    const hasTooltip = has(item, 'tooltip');
    if (hasTooltip) {
      return (
        <Tag
          key={id}
          icon={item.icon}
          color="textSecondary"
          label={
            <Tooltip title={item.tooltip} aria-label={item.tooltip}>
              <Typography variant="body2">{item.label}</Typography>
            </Tooltip>
          }
        />
      );
    }

    return (
      <Tag
        key={id}
        icon={item.icon}
        color="textSecondary"
        label={
          item.url ? (
            <Link
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              title={intl.formatMessage({ id: 'BUTTONS.OPEN_IN_NEW_TAB' })}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {item.label}
              </Typography>
            </Link>
          ) : (
            <Typography variant="body2">{item.label}</Typography>
          )
        }
      />
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      {items.map((item: any, index: any) => renderItem(index, item))}
    </Box>
  );
}

PostTags.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      // Generic item
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        icon: PropTypes.element.isRequired,
        url: PropTypes.string,
      }),

      // Location item
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        lat: PropTypes.string,
        lng: PropTypes.string,
      }),

      // Date item
      PropTypes.shape({
        start_at: PropTypes.string.isRequired,
        end_at: PropTypes.string.isRequired,
      }),
    ]),
  ).isRequired,
};

export default PostTags;
